import React, {useCallback, useMemo} from 'react';
import {Box, Heading, Grid, GridCol} from '@flixbus/honeycomb-react';
import DriverCostTable from '@/components/DriverCosts/DriverCostsTable/DriverCostsTable';
import {useUpdateBusPartnerDriverCostMutation} from '@/Pages/PartnerCosts/hooks/useUpdateBusPartnerDriverCostMutation';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import {getRegionalCostsDriverCostTableConfig} from '@/Pages/RegionalCosts/components/RegionalCostsDriverCostTab/RegionalCostsDriverCostContent/RegionalCostsDriverCostContent.data';
import {getPartnerCostsDriverCostTableConfig} from './PartnerCostsDriverCostContent.data';
import {PartnerCostsDriverCostContentProps} from './PartnerCostsDriverCostContent.types';
import styles from './PartnerCostsDriverCostContent.module.scss';
import {PartnerCostsDriverCost} from '@/Pages/PartnerCosts/store/types';

const PartnerCostsDriverCostContent: React.FC<
  PartnerCostsDriverCostContentProps
> = ({
  busPartner,
  partnerCountry,
  year,
  partnerDriverCost,
  regionalDriverCost
}) => {
  const {localUnits} = useLocalUnitsContext();

  const {updateBusPartnerDriverCost} = useUpdateBusPartnerDriverCostMutation();
  const updateVehicleCosts = useCallback(
    async (updatedCosts: PartnerCostsDriverCost) => {
      await updateBusPartnerDriverCost(updatedCosts, busPartner.id, year);
    },
    [busPartner.id, updateBusPartnerDriverCost, year]
  );

  const driverCostsConfig = useMemo(
    () => getPartnerCostsDriverCostTableConfig(partnerCountry, localUnits),
    [localUnits, partnerCountry]
  );
  const fallbackDriverCostsConfig = useMemo(
    () =>
      regionalDriverCost &&
      getRegionalCostsDriverCostTableConfig(partnerCountry, localUnits),
    [localUnits, partnerCountry, regionalDriverCost]
  );

  return (
    <>
      <div className={styles.header}>
        <Heading size={3}>
          Cost for {busPartner.name} ({year})
        </Heading>
        <div className={styles.subtitle}>
          On this page, you can define the country cost factors for specific
          partners. Partner-level values will override any existing regional
          values. If available, regional values are shown in brackets for
          reference.{' '}
        </div>
        <div className={styles.subtitle}>
          Fields marked with an * are mandatory and values must be specified at
          either regional or partner level.
        </div>
      </div>

      <Box>
        <Grid>
          <GridCol size={6}>
            <DriverCostTable
              country={partnerCountry}
              year={year}
              config={driverCostsConfig}
              driverCost={partnerDriverCost}
              fallbackConfig={fallbackDriverCostsConfig}
              fallbackDriverCost={regionalDriverCost}
              updateFn={updateVehicleCosts}
            />
          </GridCol>
        </Grid>
      </Box>
    </>
  );
};

export default React.memo(PartnerCostsDriverCostContent);
